import React, { useCallback, useEffect } from 'react';
import {
  ValidationStatusType,
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  StackLayout,
  StatusBanner,
  styled,
} from '@leagueplatform/genesis-core';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';

type MasonryFormNodeProperties = {
  formId: string;
  statusMessage?: string;
  statusType?: ValidationStatusType;
};

export type MasonryFormNodeActions = {};

export type MasonryFormNode = MasonryEngineNode<
  'form',
  MasonryFormNodeProperties,
  MasonryFormNodeActions,
  {
    layout: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryFormRendererProps =
  MasonryEngineNodeRendererProps<MasonryFormNode>;

const StyledForm = styled('form', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  maxWidth: '800px',
});

export const MasonryFormRenderer = ({
  css,
  layout,
  className,
  engineClassName,
  rootElementRef,
  locatorId,
  statusMessage,
  statusType,
  nodeId,
}: MasonryFormRendererProps) => {
  const statusBannerRef = React.useRef<HTMLDivElement>(null);
  const invokeAction = useInvokeAction();

  const focusStatusBanner = useCallback(() => {
    if (statusMessage && statusBannerRef.current) {
      statusBannerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [statusBannerRef, statusMessage]);

  useEffect(() => {
    // This useEffect will run when the component mounts and whenever the statusMessage changes
    if (statusMessage && statusBannerRef.current) {
      focusStatusBanner();
    }
  }, [focusStatusBanner, statusMessage]);

  const handleHideFormStatusBanner = useCallback(() => {
    invokeAction({
      type: 'setFormStatusBanner',
      namespace: 'default',
      payload: {
        formNodeId: nodeId,
        statusMessage: undefined,
        statusType: undefined,
      },
    });
  }, [invokeAction, nodeId]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    // Focus on status banner whenever form is submitted to focus the exiting status banner
    handleHideFormStatusBanner();

    // Masonry form renderer is form element to wrap around form field and will not handle submit
    // action. Submit action will be handled by the CTA renderers like buttons.
    event.preventDefault();
  };

  return (
    <StyledForm
      onSubmit={handleFormSubmit}
      className={formatClassNames(engineClassName, className)}
      ref={rootElementRef}
      noValidate
      data-testid={locatorId}
      css={css}
    >
      <StackLayout
        orientation="vertical"
        horizontalAlignment="stretch"
        spacing="$one"
      >
        {statusMessage && (
          <StatusBanner
            ref={statusBannerRef}
            status={statusType || 'error'}
            title={statusMessage}
            onDismissClick={handleHideFormStatusBanner}
            showIcon
          />
        )}
        {layout}
      </StackLayout>
    </StyledForm>
  );
};
